import apiClient from '../utils/apiClient';

export interface LiturgicalYear {
  liturgical_year_id: number;
  liturgical_year: string;
  start_date: string;
  end_date: string;
  created_at?: string;
  updated_at?: string;
}

export interface LiturgicalCalendarService {
  calendar_id: number;
  liturgical_year: string;
  liturgy_date: string;
  liturgy_name: string;
  ot_reading: string | null;
  nt_reading: string | null;
  gospel_reading: string | null;
  psalm_reading: string | null;
  season: string;
  is_special: boolean | null;
}

export interface NewLiturgicalYearData {
  liturgical_year: string;
  start_date: string;
  end_date: string;
}

export interface NewServiceData {
  liturgical_year_id: number;
  liturgy_date: string;
  time: string;
  liturgy_name: string;
  calendar_id?: number;
  church_id?: number;
  service_def_id?: number;
  ot_reading?: string;
  nt_reading?: string;
  gospel_reading?: string;
  psalm_reading?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export const liturgicalYearService = {
  async getAllLiturgicalYears(): Promise<LiturgicalYear[]> {
    try {
      const response = await apiClient.get<ApiResponse<LiturgicalYear[]>>('/liturgical-years');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch liturgical years');
    } catch (error) {
      console.error('Error fetching liturgical years:', error);
      throw error;
    }
  },

  async getLiturgicalYearsWithServices(): Promise<{ liturgical_year: string; service_count: number }[]> {
    try {
      const response = await apiClient.get<ApiResponse<{ liturgical_year: string; service_count: number }[]>>('/liturgical-years/with-services');
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch liturgical years with services');
    } catch (error) {
      console.error('Error fetching liturgical years with services:', error);
      throw error;
    }
  },

  async createLiturgicalYear(data: NewLiturgicalYearData): Promise<LiturgicalYear> {
    try {
      const response = await apiClient.post<ApiResponse<LiturgicalYear>>('/liturgical-years', data);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create liturgical year');
    } catch (error) {
      console.error('Error creating liturgical year:', error);
      throw error;
    }
  },

  async updateLiturgicalYear(id: number, data: Partial<NewLiturgicalYearData>): Promise<LiturgicalYear> {
    try {
      const response = await apiClient.put<ApiResponse<LiturgicalYear>>(`/liturgical-years/${id}`, data);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update liturgical year');
    } catch (error) {
      console.error('Error updating liturgical year:', error);
      throw error;
    }
  },

  async deleteLiturgicalYear(id: number): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`/liturgical-years/${id}`);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete liturgical year');
      }
    } catch (error) {
      console.error('Error deleting liturgical year:', error);
      throw error;
    }
  },

  async getServicesForLiturgicalYear(liturgicalYear: string): Promise<LiturgicalCalendarService[]> {
    try {
      const response = await apiClient.get<ApiResponse<LiturgicalCalendarService[]>>(`/liturgical-years/${liturgicalYear}/services`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch services for liturgical year');
    } catch (error) {
      console.error('Error fetching services for liturgical year:', error);
      throw error;
    }
  },

  async getUserServicesForLiturgicalYear(liturgicalYear: string): Promise<any[]> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(`/liturgical-years/${liturgicalYear}/user-services`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch user services for liturgical year');
    } catch (error) {
      console.error('Error fetching user services for liturgical year:', error);
      throw error;
    }
  },

  async createService(data: NewServiceData): Promise<any> {
    try {
      const response = await apiClient.post<ApiResponse<any>>('/liturgical-years/services', data);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to create service');
    } catch (error) {
      console.error('Error creating service:', error);
      throw error;
    }
  },

  async updateService(serviceId: number, data: Partial<NewServiceData>): Promise<any> {
    try {
      const response = await apiClient.put<ApiResponse<any>>(`/liturgical-years/services/${serviceId}`, data);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to update service');
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  },

  async deleteService(serviceId: number): Promise<void> {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`/liturgical-years/services/${serviceId}`);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete service');
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      throw error;
    }
  },

  async getServiceDefinitionsForChurch(churchId: number): Promise<any[]> {
    try {
      const response = await apiClient.get<ApiResponse<any[]>>(`/liturgical-years/churches/${churchId}/service-definitions`);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch service definitions for church');
    } catch (error) {
      console.error('Error fetching service definitions for church:', error);
      throw error;
    }
  }
};